import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import DashboardLayout from './Layouts/DashboardLayout';
import { CameraAlt } from '@mui/icons-material';
import logger from '../utils/logger';

const PageContainer = styled(Box)(({ theme }) => ({
  padding: '60px 80px 100px',
  backgroundColor: '#ffffff',
  minHeight: '100vh',
  [theme.breakpoints.down('lg')]: {
    padding: '50px 60px 80px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '40px 40px 70px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '32px 24px 60px'
  }
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto'
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: 'clamp(28px, 4vw, 36px)',
  fontWeight: 400,
  color: '#1a1a1a',
  marginBottom: '8px',
  letterSpacing: '-0.5px'
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: '#666666',
  marginBottom: '48px',
  lineHeight: 1.6
}));

// Logo Upload Section
const LogoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  marginBottom: '48px',
  paddingBottom: '48px',
  borderBottom: '1px solid #e5e7eb'
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  backgroundColor: '#f8f9fa',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  border: '1px solid #e5e7eb'
}));

const LogoImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover'
}));

const LogoPlaceholder = styled(Box)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: '#9ca3af',
  textAlign: 'center'
}));

const UploadButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '8px 20px',
  borderRadius: '6px',
  border: '1px solid #d1d5db',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    borderColor: '#9ca3af'
  }
}));

// Form Section
const FormSection = styled(Stack)(({ theme }) => ({
  gap: '32px'
}));

const FormRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '32px'
  }
}));

const FormField = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  color: '#1a1a1a'
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    fontSize: '14px',
    '& fieldset': {
      borderColor: '#d1d5db'
    },
    '&:hover fieldset': {
      borderColor: '#9ca3af'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1a1a1a',
      borderWidth: '1px'
    }
  },
  '& .MuiInputBase-input': {
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    fontWeight: 400,
    color: '#1a1a1a',
    padding: '12px 14px'
  }
}));

// Action Buttons
const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
  marginTop: '16px',
  paddingTop: '32px',
  borderTop: '1px solid #e5e7eb',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse'
  }
}));

const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#666666',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '10px 24px',
  borderRadius: '6px',
  border: '1px solid #d1d5db',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    borderColor: '#9ca3af'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%'
  }
}));

const UpdateButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1a1a1a',
  color: '#ffffff',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '10px 32px',
  borderRadius: '6px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#333333'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%'
  }
}));

export default function MyAccountPage() {
  const [formData, setFormData] = useState({
    businessName: 'Medical shop',
    industryType: 'Dental clinic',
    startDate: '2025-01-02',
    endDate: '2025-01-02',
    email: 'xyz@gmail.com',
    address: 'Office Floor, Bestech, Sector 66, Sahibzada Ajit Singh Nagar, Punjab 160066',
    website: 'www.visibeen.com'
  });

  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdate = () => {
    logger.log('Update clicked', formData);
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const url = URL.createObjectURL(file);
    setLogoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
  };

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  const handleCancel = () => {
    logger.log('Cancel clicked');
  };

  return (
    <DashboardLayout>
      <PageContainer>
        <ContentWrapper>
          <PageTitle>Account Information</PageTitle>
          <PageSubtitle>
            Manage your business profile and account settings
          </PageSubtitle>

          {/* Logo Upload */}
          <LogoSection>
            <LogoContainer>
              {logoPreviewUrl ? (
                <LogoImage src={logoPreviewUrl} alt="Business Logo" />
              ) : (
                <LogoPlaceholder>
                  <CameraAlt sx={{ fontSize: 32, color: '#d1d5db', mb: 1 }} />
                  <div>Logo</div>
                </LogoPlaceholder>
              )}
            </LogoContainer>
            <Box>
              <UploadButton onClick={handleLogoClick}>
                Upload Logo
              </UploadButton>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleLogoFileSelected}
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  mt: 1, 
                  color: '#9ca3af',
                  fontSize: '12px'
                }}
              >
                JPG, PNG or SVG. Max size 2MB
              </Typography>
            </Box>
          </LogoSection>

          {/* Form Fields */}
          <FormSection>
            <FormField>
              <FieldLabel>Business Name *</FieldLabel>
              <StyledTextField
                fullWidth
                value={formData.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
                placeholder="Enter business name"
              />
            </FormField>

            <FormField>
              <FieldLabel>Industry Type *</FieldLabel>
              <StyledTextField
                fullWidth
                value={formData.industryType}
                onChange={(e) => handleInputChange('industryType', e.target.value)}
                placeholder="Select industry type"
              />
            </FormField>

            <FormRow>
              <FormField>
                <FieldLabel>Start Date *</FieldLabel>
                <StyledTextField
                  fullWidth
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                />
              </FormField>
              <FormField>
                <FieldLabel>End Date *</FieldLabel>
                <StyledTextField
                  fullWidth
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                />
              </FormField>
            </FormRow>

            <FormField>
              <FieldLabel>Email Address *</FieldLabel>
              <StyledTextField
                fullWidth
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter email address"
              />
            </FormField>

            <FormField>
              <FieldLabel>Address *</FieldLabel>
              <StyledTextField
                fullWidth
                multiline
                rows={3}
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Enter address"
              />
            </FormField>

            <FormField>
              <FieldLabel>Website *</FieldLabel>
              <StyledTextField
                fullWidth
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="Enter website URL"
              />
            </FormField>

            <ActionButtons>
              <CancelButton onClick={handleCancel}>
                Cancel
              </CancelButton>
              <UpdateButton onClick={handleUpdate}>
                Update
              </UpdateButton>
            </ActionButtons>
          </FormSection>
        </ContentWrapper>
      </PageContainer>
    </DashboardLayout>
  );
}